/**
 * AIOO Facets UX — the single facet engine for collection and search pages.
 *
 * One fetch path for every filter entry point:
 *   - this file's change listener on the filter form               -> load()
 *   - theme.min.js form/chip handlers call window.ajaxfilter(url)  -> load()
 *   - collection-model-filter-ajax.js model pills                  -> AIOO_facets.load()
 *   - back/forward                                                  -> one popstate handler here
 * The form listener does not depend on theme.collectionView. That constructor
 * used to be stubbed on in-stock-iphones, which left the skeleton up forever.
 * Requests use the Section Rendering API (?section_id=) with a full-page fallback,
 * are debounced, and abort the previous request. The grid shows a skeleton while
 * loading; the sidebar is re-rendered so counts never go stale and zero-result
 * options disappear, while open groups, "show more", list search, scroll and focus survive.
 *
 * Does NOT alter hub -> subcollection MBF routing (collection-model-filter-ajax.js keeps it).
 */
(function (window, document) {
  'use strict';

  var DRAWER_SEL = '#CollectionFiltersDrawer, .sidebar.filterbar';
  var GRID_SEL = '#GridContainer, .productList .grid-products';
  var BODY_LOCK = 'aioo-facets-scroll-lock';
  var SWATCH_COLLAPSE_LIMIT = 12;
  var LIST_COLLAPSE_AT = 5;
  var LIST_SEARCH_AT = 10;
  var DEBOUNCE_MS = 200;
  var SKELETON_DELAY_MS = 120;
  var REQUEST_TIMEOUT_MS = 12000;
  var PRICE_COMMIT_MS = 400;
  var LIST_SKIP_SEL = '.filter-color, .flPrice, .mbf-sidebar-widget, .model-links-widget, .condition-filter-widget';
  var lastFocus = null;

  var core = {
    seq: 0,
    ctrl: null,
    timer: null,
    watchdog: null,
    userOpen: {},
    lastUrl: window.location.pathname + window.location.search
  };

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  /* Writing identical text still replaces the text node, which re-fires the sidebar
     MutationObserver; skipping no-op writes is what keeps that observer from looping. */
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }
  function setHidden(el, hidden) {
    if (el && el.hidden !== !!hidden) el.hidden = !!hidden;
  }

  function getDrawer() {
    return qs(DRAWER_SEL);
  }

  function i18n(key, fallback) {
    var head = qs('.aioo-facets-head');
    return (head && head.getAttribute('data-i18n-' + key)) || fallback;
  }

  /* Desktop inline sidebar is a pure CSS decision (aioo-facets-ux.css); JS only reads it. */
  function isInline() {
    var d = qs('#CollectionFiltersDrawer');
    if (!d || !window.getComputedStyle) return false;
    return window.getComputedStyle(d).position === 'sticky';
  }

  function isDrawerOpen() {
    var d = getDrawer();
    return !!(d && d.classList.contains('active')) && !isInline();
  }

  /* ---- Body scroll lock for mobile off-canvas drawer ---- */
  function lockBody(lock) {
    if (lock) {
      if (document.body.dataset.aiooScrollY == null) {
        document.body.dataset.aiooScrollY = String(window.scrollY || 0);
      }
      document.documentElement.classList.add(BODY_LOCK);
      document.body.classList.add(BODY_LOCK);
    } else {
      var y = parseInt(document.body.dataset.aiooScrollY || '0', 10);
      document.documentElement.classList.remove(BODY_LOCK);
      document.body.classList.remove(BODY_LOCK);
      delete document.body.dataset.aiooScrollY;
      if (y) window.scrollTo(0, y);
    }
  }

  /* ---- Grid loading: soft lock at once, skeleton only if the request is not instant ---- */
  function setGridLoading(on) {
    clearTimeout(setGridLoading._t);
    clearTimeout(setGridLoading._safety);
    qsa(GRID_SEL).forEach(function (grid) {
      grid.classList.toggle('aioo-facets-grid--loading', !!on);
      grid.setAttribute('aria-busy', on ? 'true' : 'false');
      if (!on) grid.classList.remove('aioo-facets-grid--skeleton');
    });
    if (on) {
      setGridLoading._t = setTimeout(function () {
        qsa(GRID_SEL).forEach(function (grid) {
          if (grid.classList.contains('aioo-facets-grid--loading')) grid.classList.add('aioo-facets-grid--skeleton');
        });
      }, SKELETON_DELAY_MS);
      /* If nothing ever calls load(), the skeleton used to stay forever. */
      setGridLoading._safety = setTimeout(function () {
        setGridLoading(false);
      }, REQUEST_TIMEOUT_MS + 1000);
    }
  }

  /* Drop the full-screen "Filtrē..." veil, grid lock, and (on back) the drawer.
     bfcache restores the page with that veil still active, which looks frozen. */
  function clearCollectionChrome(opts) {
    opts = opts || {};
    qsa('.mbf-loading-overlay.is-active').forEach(function (el) {
      el.classList.remove('is-active');
    });
    document.body.classList.remove('loading', 'hideOverly');
    setGridLoading(false);
    if (opts.closeDrawer) {
      qsa('.filterbar.active').forEach(function (el) {
        el.classList.remove('active');
      });
      lockBody(false);
    }
  }
  window.AIOO_clearCollectionChrome = clearCollectionChrome;

  window.addEventListener('pageshow', function (e) {
    clearCollectionChrome({ closeDrawer: !!e.persisted });
    if (e.persisted) {
      window.AIOO_bfcacheHold = true;
      setTimeout(function () { window.AIOO_bfcacheHold = false; }, 400);
    }
  });

  /* ---- Drawer (mobile/tablet) vs inline sticky sidebar (desktop) ---- */
  function syncDrawerChrome() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (isInline()) {
      var wasAsked = drawer.classList.contains('active');
      if (wasAsked) drawer.classList.remove('active');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      drawer.removeAttribute('aria-hidden');
      if (document.body.classList.contains(BODY_LOCK)) lockBody(false);
      if (wasAsked) {
        var first = qs('.sidebar_tags .sidebar_widget button, .sidebar_tags .sidebar_widget input:not([type="hidden"]), .sidebar_tags .sidebar_widget a', drawer);
        if (first) {
          try { first.focus({ preventScroll: true }); } catch (_f) { first.focus(); }
        }
      }
      return;
    }
    var open = drawer.classList.contains('active');
    lockBody(open);
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', open ? 'true' : 'false');
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (open) {
      lastFocus = document.activeElement;
      var closeBtn = drawer.querySelector('.closeFilter');
      if (closeBtn && typeof closeBtn.focus === 'function') {
        try { closeBtn.focus(); } catch (_e) { /* ignore */ }
      }
      updateChrome();
    } else if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (_e2) { /* ignore */ }
      lastFocus = null;
    }
  }

  function watchDrawer() {
    var drawer = getDrawer();
    if (!drawer || !window.MutationObserver) return;
    var obs = new MutationObserver(function () { syncDrawerChrome(); });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    syncDrawerChrome();
  }

  /* Sticky offset under the theme's fixed header (#header gets .stickyH after 145px). */
  function updateStickyTop() {
    var drawer = qs('#CollectionFiltersDrawer');
    if (!drawer) return;
    var top = 16;
    var header = qs('#header');
    if (header && window.getComputedStyle) {
      var pos = window.getComputedStyle(header).position;
      if (pos === 'fixed' || pos === 'sticky') {
        var r = header.getBoundingClientRect();
        if (r.bottom > 0) top = Math.round(r.bottom) + 16;
      }
    }
    if (top !== updateStickyTop._v) {
      updateStickyTop._v = top;
      drawer.style.setProperty('--aioo-facets-top', top + 'px');
    }
  }

  /* ---- Result counts ---- */
  function isElementVisible(el) {
    if (!el || el.hidden) return false;
    var style = window.getComputedStyle ? window.getComputedStyle(el) : null;
    if (style) {
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (parseFloat(style.opacity || '1') === 0) return false;
    }
    if (style && style.position !== 'fixed' && el.offsetParent === null) return false;
    var r = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (r && (r.width === 0 || r.height === 0)) return false;
    return true;
  }

  function countVisibleCards() {
    var primary = qsa('#GridContainer .gitem:not(.small--tc), #GridContainer .list-view-item');
    var n = primary.filter(isElementVisible).length;
    if (n) return n;
    return qsa('#GridContainer .grid-view-item, #GridContainer .grid-view-item--phone').filter(isElementVisible).length;
  }

  function readTotalResults() {
    var el = qs('.sidebar_tags [data-aioo-total-results]');
    if (el) {
      var n = parseInt(el.getAttribute('data-aioo-total-results') || '', 10);
      if (!isNaN(n)) return n;
    }
    var host = qs('.filters-toolbar__product-count[data-aioo-total]') || qs('#aioo-picker-cta[data-product-count]');
    if (host) {
      var m = String(host.getAttribute('data-aioo-total') || host.getAttribute('data-product-count') || '').match(/(\d+)/);
      if (m) return parseInt(m[1], 10);
    }
    return countVisibleCards();
  }

  function syncVisibleProductCount() {
    var el = qs('.filters-toolbar__product-count[data-aioo-count-host]');
    if (!el) return;
    var n = countVisibleCards();
    var showing = el.getAttribute('data-aioo-showing') || '';
    var results = el.getAttribute('data-aioo-results') || '';
    var total = parseInt(el.getAttribute('data-aioo-total') || '0', 10);
    var of = el.getAttribute('data-aioo-of') || '';
    var countText = total > n && n > 0 && of ? n + ' ' + of + ' ' + total : String(n);
    setText(el, (showing + ' ' + countText + ' ' + results).replace(/\s+/g, ' ').trim());
    var empty = n === 0;
    qsa('.aioo-load-more[data-load-more-wrap], .infinitpaginOuter, .infinitpagin, .loadMore, .infinite').forEach(function (node) {
      setHidden(node, empty);
    });
  }

  /* ---- Active state (URL is the source of truth for server facets) ---- */
  function activeState() {
    var facets = 0;
    var model = false;
    var price = false;
    try {
      new URL(window.location.href).searchParams.forEach(function (v, k) {
        if (k === 'aioo_model') { if (v) model = true; return; }
        if (k.indexOf('filter.') !== 0 || v === '') return;
        if (k.indexOf('compatible_model') !== -1) { model = true; return; }
        if (/\.price\.(gte|lte)$/.test(k)) { price = true; return; }
        facets += 1;
      });
    } catch (_e) { /* ignore */ }
    if (price) facets += 1;
    var client = qsa('.brand-filter-checkbox:checked').length;
    var cond = qs('.condition-filter-radio:checked');
    if (cond && cond.value !== 'all') client += 1;
    return { facets: facets, model: model ? 1 : 0, client: client, total: facets + (model ? 1 : 0) + client };
  }

  function updateFilterBadge(st) {
    var badge = qs('.aioo-filter-badge');
    if (!badge) return;
    var n = st.facets + st.client;
    setText(badge, String(n));
    setHidden(badge, n === 0);
    badge.setAttribute('aria-hidden', n === 0 ? 'true' : 'false');
  }

  /* products_count also counts cards the page hides (sold-out phones), so when the whole
     result set is on this page the visible cards are the honest number. */
  function currentResultCount(st) {
    if (st.client > 0) return countVisibleCards();
    var total = readTotalResults();
    var rendered = qsa('#GridContainer .gitem:not(.small--tc), #GridContainer .list-view-item').length;
    return (rendered && rendered >= total && countVisibleCards()) || total;
  }

  function updateApplyFooter(st) {
    var foot = qs('.aioo-facets-apply');
    if (!foot) return;
    var label = foot.querySelector('.aioo-facets-apply__label');
    var tpl = foot.getAttribute('data-label-tpl') || 'Rādīt @@';
    setText(label, tpl.replace(/@@/g, String(currentResultCount(st))));
  }

  function updateChrome() {
    var st = activeState();
    qsa('[data-aioo-facets-reset]').forEach(function (btn) { setHidden(btn, st.total === 0); });
    updateFilterBadge(st);
    updateApplyFooter(st);
    return st;
  }

  function announceResults() {
    var live = qs('[data-aioo-facets-live]');
    if (!live) return;
    var tpl = i18n('results', '@@');
    setText(live, tpl.replace(/@@/g, String(currentResultCount(activeState()))));
  }

  function bindApplyFooter() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.aioo-facets-apply__btn');
      if (!btn) return;
      e.preventDefault();
      var drawer = getDrawer();
      if (drawer) drawer.classList.remove('active');
      syncDrawerChrome();
      var grid = qs('#GridContainer');
      if (grid) {
        var top = grid.getBoundingClientRect().top;
        if (top < 0) window.scrollTo(0, Math.max(0, (window.scrollY || 0) + top - 96));
      }
    });
  }

  /* ---- Reset all ---- */
  function resetAll() {
    var u = new URL(window.location.href);
    var drop = [];
    var hadModel = false;
    u.searchParams.forEach(function (_v, k) {
      if (k.indexOf('filter.') === 0 || k === 'aioo_model' || k === 'page' || k === 'condition') {
        if (k === 'aioo_model' || k.indexOf('compatible_model') !== -1) hadModel = true;
        drop.push(k);
      }
    });
    drop.forEach(function (k) { u.searchParams.delete(k); });
    if (hadModel) {
      try { localStorage.removeItem('AIOO_selectedDevice'); } catch (_ls) { /* ignore */ }
    }
    if (typeof window.AIOO_resetBrandFilter === 'function') window.AIOO_resetBrandFilter();
    if (typeof window.AIOO_resetConditionFilter === 'function') window.AIOO_resetConditionFilter();
    if (drop.length) {
      load(u.pathname + u.search + u.hash, { reset: true, immediate: true });
    } else {
      updateChrome();
      announceResults();
    }
  }

  function bindReset() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-aioo-facets-reset]');
      if (!btn) return;
      e.preventDefault();
      resetAll();
    });
  }

  /* ---- Color swatches (circular) ---- */
  function swatchLuminance(el) {
    if (!el) return 0.5;
    var bg = window.getComputedStyle(el).backgroundColor || '';
    var m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return 0.5;
    return (0.299 * parseInt(m[1], 10) + 0.587 * parseInt(m[2], 10) + 0.114 * parseInt(m[3], 10)) / 255;
  }

  function enhanceColorSwatches(root) {
    qsa('.sidebar_widget.filter-color .aioo-color-filter__row, .sidebar_widget.filter-color label.fl-checkbox', root).forEach(function (row) {
      if (row.closest('.aioo-color-filter__originals')) return;
      if (row.classList.contains('aioo-swatch-chip')) return;
      row.classList.add('aioo-swatch-chip');
      var sw = row.querySelector('.aioo-color-filter__swatch');
      var input = row.querySelector('input[type="checkbox"]');
      if (input && input.disabled) {
        row.classList.add('is-disabled');
        row.setAttribute('aria-disabled', 'true');
      }
      if (input && input.checked) row.classList.add('is-active');

      var lbl = row.querySelector('.aioo-color-filter__label');
      if (!lbl) {
        lbl = document.createElement('span');
        lbl.className = 'aioo-color-filter__label';
        Array.prototype.filter.call(row.childNodes, function (n) { return n.nodeType === 3; })
          .forEach(function (n) { lbl.appendChild(n); });
        row.appendChild(lbl);
      }
      var tip = (lbl.textContent || '').replace(/\(\d+\)\s*$/, '').trim();
      if (tip) row.setAttribute('title', tip);
      if (input && !input.getAttribute('aria-label') && tip) {
        input.setAttribute('aria-label', tip);
      }
      if (sw) {
        sw.setAttribute('aria-hidden', 'true');
        if (swatchLuminance(sw) > 0.72 && !sw.classList.contains('has-split') && !sw.classList.contains('has-triple')) {
          row.classList.add('is-light');
        }
      }
    });
  }

  function getColorCollapseButton(widget) {
    var btn = widget.querySelector('.aioo-color-collapse');
    if (btn) return btn;
    var panel = widget.querySelector('.widget-content, ul.flOpt');
    if (!panel) return null;
    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'aioo-color-collapse';
    btn.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', function () {
      var expanded = widget.classList.toggle('swatches-expanded');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      applyColorSwatchCollapse(widget.parentElement || document);
    });
    panel.insertAdjacentElement('afterend', btn);
    return btn;
  }

  function applyColorSwatchCollapse(root) {
    qsa('.sidebar_widget.filter-color', root).forEach(function (widget) {
      var button = getColorCollapseButton(widget);
      var chips = qsa('label.aioo-swatch-chip', widget).filter(function (row) {
        return !row.closest('.aioo-color-filter__originals');
      });
      chips.forEach(function (row) {
        row.classList.remove('aioo-swatch-chip--collapsed');
      });
      if (!button) {
        widget.classList.remove('has-collapsible-swatches', 'swatches-expanded');
        return;
      }
      var visible = chips.filter(function (row) {
        return !row.hidden && !row.classList.contains('aioo-oos-facet');
      });
      if (visible.length <= SWATCH_COLLAPSE_LIMIT) {
        widget.classList.remove('has-collapsible-swatches', 'swatches-expanded');
        setHidden(button, true);
        return;
      }
      widget.classList.add('has-collapsible-swatches');
      var expanded = widget.classList.contains('swatches-expanded');
      var hiddenCount = 0;
      if (!expanded) {
        visible.forEach(function (row, idx) {
          var input = row.querySelector('input[type="checkbox"]');
          if ((input && input.checked) || idx < SWATCH_COLLAPSE_LIMIT) return;
          row.classList.add('aioo-swatch-chip--collapsed');
          hiddenCount += 1;
        });
      }
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      setText(button, expanded
        ? i18n('less', 'Rādīt mazāk')
        : i18n('more', 'Rādīt vēl (@@)').replace(/@@/g, String(hiddenCount)));
      setHidden(button, !expanded && hiddenCount === 0);
    });
  }

  /* Short nouns for custom.subtype. Mirrors utils/title_rules.py SUBTYPE_SHORT. */
  var SUBTYPE_SHORT = {
    'viedtālruņa aizmugurējais vāciņš': 'Vāciņš',
    'viedtālruņa atveramais maciņš': 'Maciņš',
    'viedtālruņa aizsargstikls': 'Aizsargstikls',
    'viedtālruņa kameras aizsargstikls': 'Kameras aizsargstikls',
    'viedtālruņa aizsargplēve': 'Aizsargplēve',
    'bezvadu austiņu apvalks': 'Austiņu apvalks',
    'planšetdatora universālais maciņš': 'Planšetes maciņš',
    'planšetdatora atveramais maciņš': 'Planšetes maciņš',
    'planšetdatora vāciņš': 'Planšetes vāciņš',
    'viedtālruņa universālais maciņš': 'Universāls maciņš',
    'viedtālruņa turētājs automašīnai': 'Auto turētājs',
    'viedtālruņa pirkstu turētājs': 'Pirkstu turētājs',
    'viedtālruņa pirksta turētājs': 'Pirkstu turētājs',
    'viedtālruņa karšu kabatiņa': 'Karšu kabatiņa'
  };

  function subtypeKey(text) {
    return String(text || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('lv');
  }

  function subtypeLabel(key) {
    if (SUBTYPE_SHORT[key]) return SUBTYPE_SHORT[key];
    if (!key) return '';
    return key.charAt(0).toLocaleUpperCase('lv') + key.slice(1);
  }

  /* Shopify lists "Vāciņš" and "vāciņš" as two values. One chip, both params. */
  function mergeSubtypeWidgets(root) {
    qsa('.sidebar_widget.filter-chips[data-aioo-facet-key*="custom.subtype"]', root).forEach(function (widget) {
      var list = widget.querySelector('ul.widget-content');
      if (!list || list.getAttribute('data-aioo-subtype-merged') === '1') return;
      list.setAttribute('data-aioo-subtype-merged', '1');
      var labels = Array.from(list.querySelectorAll('label.fl-checkbox'));
      if (!labels.length) return;
      var groups = {};
      var order = [];
      labels.forEach(function (lab) {
        var input = lab.querySelector('input.custCheck');
        if (!input) return;
        var shown = lab.querySelector('.aioo-color-filter__label');
        var key = subtypeKey(shown ? shown.textContent : input.value);
        if (!key) return;
        if (!groups[key]) {
          groups[key] = { key: key, items: [], count: 0, active: false };
          order.push(key);
        }
        var group = groups[key];
        group.items.push(input);
        var n = parseInt(input.getAttribute('data-count'), 10);
        if (!isNaN(n)) group.count += n;
        if (input.checked) group.active = true;
      });
      order.sort(function (a, b) { return groups[b].count - groups[a].count; });
      order.forEach(function (key, idx) {
        var group = groups[key];
        group.items.forEach(function (input) {
          var lab = input.closest('label');
          if (!lab) return;
          lab.hidden = true;
          lab.classList.add('aioo-subtype-source');
        });
        var row = document.createElement('label');
        row.className = 'fl-checkbox aioo-facet-chip';
        row.setAttribute('for', 'Filter-subtype-' + idx);
        if (group.active) row.classList.add('is-active');
        var master = document.createElement('input');
        master.type = 'checkbox';
        master.className = 'aioo-subtype-master';
        master.id = 'Filter-subtype-' + idx;
        master.checked = group.active;
        master.setAttribute('aria-label', subtypeLabel(key));
        row.appendChild(master);
        row.appendChild(document.createElement('span')).className = 'checkbox';
        var text = document.createElement('span');
        text.className = 'aioo-color-filter__label';
        text.textContent = subtypeLabel(key);
        row.appendChild(text);
        if (group.count > 0) {
          var cnt = document.createElement('span');
          cnt.className = 'prcnt';
          cnt.textContent = ' (' + group.count + ')';
          row.appendChild(cnt);
        }
        master.addEventListener('change', function (e) {
          e.stopPropagation();
          var changed = null;
          group.items.forEach(function (input) {
            if (input.checked === master.checked) return;
            input.checked = master.checked;
            changed = changed || input;
          });
          row.classList.toggle('is-active', !!master.checked);
          var fire = changed || group.items[0];
          if (fire) fire.dispatchEvent(new Event('change', { bubbles: true }));
        });
        list.appendChild(row);
      });
    });
  }

  /* ---- Chip filters: vendor / material / subtype ---- */
  function enhanceFacetLists(root) {
    root = root || document;
    mergeSubtypeWidgets(root);
    enhanceColorSwatches(root);
    applyColorSwatchCollapse(root);
    qsa('.sidebar_widget.filter-chips label.fl-checkbox', root).forEach(function (lab) {
      lab.classList.add('aioo-facet-chip');
      var input = lab.querySelector('input[type="checkbox"]');
      if (!input) return;
      lab.classList.toggle('is-active', !!input.checked);
      if (input.disabled || lab.classList.contains('disabled')) {
        lab.classList.add('is-disabled');
        lab.setAttribute('aria-disabled', 'true');
      }
    });
  }

  /* Same merge as theme.collectionView: the form owns its fields, live URL
     keeps params the form does not render (model). condition/cp are reapplied
     in buildTarget so a phone subtype can override a contradictory condition. */
  function formFilterUrl() {
    var form = qs('#CollectionFiltersForm') || qs('.flForm');
    if (!form) return window.location.pathname + window.location.search;
    var params = new URLSearchParams(new FormData(form));
    var owned = {};
    qsa('[name]', form).forEach(function (el) {
      if (el.name) owned[el.name] = true;
    });
    try {
      var live = new URL(window.location.href);
      live.searchParams.forEach(function (val, key) {
        if (owned[key] || key === 'page' || key === 'view' || key === 'condition' || key === 'cp') return;
        if (key.indexOf('filter.') === 0 || key === 'aioo_model' || key === 'sort_by') {
          params.append(key, val);
        }
      });
    } catch (_e) { /* form data alone */ }
    var q = params.toString();
    return window.location.pathname + (q ? '?' + q : '');
  }

  function bindChipToggle() {
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.matches) return;
      if (!input.matches('.flForm .custCheck, .flForm .prRange, #SortBy')) return;
      var lab = input.closest('label.fl-checkbox, label.aioo-color-filter__row');
      if (lab) lab.classList.toggle('is-active', !!input.checked);
      applyColorSwatchCollapse(document);
      var url = formFilterUrl();
      if (url) load(url, {});
    });
  }

  /* ---- Long option lists: "show more" over 5, in-list search over 10 ---- */
  function widgetKey(w) {
    var k = w.getAttribute('data-aioo-facet-key');
    if (k) return k;
    var m = String(w.className || '').match(/\b(mbf-sidebar-widget|model-links-widget|condition-filter-widget|brand-filter-widget|filter-color|flPrice)\b/);
    return m ? m[1] : (w.id || '');
  }

  function normText(s) {
    s = String(s || '').replace(/\(\d+\)\s*$/, '').toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return s.replace(/\s+/g, ' ').trim();
  }

  function listRows(widget) {
    var list = widget.querySelector('ul.flOpt');
    if (!list) return [];
    return qsa('label.fl-checkbox, label.brand-filter-opt', list).map(function (lab) {
      return lab.parentElement && lab.parentElement.tagName === 'LI' ? lab.parentElement : lab;
    });
  }

  function rowAvailable(row) {
    if (row.hidden || row.classList.contains('aioo-oos-facet')) return false;
    var lab = row.tagName === 'LABEL' ? row : row.querySelector('label');
    return !(lab && (lab.hidden || lab.classList.contains('aioo-oos-facet')));
  }

  function rowText(row) {
    var lbl = row.querySelector('.aioo-color-filter__label');
    if (lbl) return lbl.textContent;
    var clone = row.cloneNode(true);
    qsa('.prcnt', clone).forEach(function (n) { n.remove(); });
    return clone.textContent;
  }

  function widgetTitle(w) {
    var t = w.querySelector('.widget-title');
    return t ? normText(t.textContent).replace(/\s*\d+$/, '') : '';
  }

  function buildListSearch(w, list) {
    var box = document.createElement('div');
    box.className = 'aioo-facet-search';
    var input = document.createElement('input');
    input.type = 'search';
    input.className = 'aioo-facet-search__input';
    input.autocomplete = 'off';
    input.spellcheck = false;
    var ph = i18n('search', 'Meklēt sarakstā');
    input.placeholder = ph;
    var title = (w.querySelector('.widget-title') || {}).textContent || '';
    input.setAttribute('aria-label', (ph + ': ' + title.replace(/\s+/g, ' ')).trim());
    box.appendChild(input);
    var empty = document.createElement('p');
    empty.className = 'aioo-facet-search__empty';
    empty.hidden = true;
    empty.setAttribute('role', 'status');
    empty.textContent = i18n('empty', 'Nekas nav atrasts');
    list.parentNode.insertBefore(box, list);
    list.parentNode.insertBefore(empty, list.nextSibling);
    input.addEventListener('input', function () { refreshList(w); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
      if (e.key === 'Escape' && input.value) {
        e.preventDefault();
        e.stopPropagation();
        input.value = '';
        refreshList(w);
      }
    });
    return box;
  }

  function buildListMore(w, list) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'aioo-facet-more';
    btn.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    var label = document.createElement('span');
    label.className = 'aioo-facet-more__label';
    var chev = document.createElement('span');
    chev.className = 'aioo-facet-more__chev';
    chev.setAttribute('aria-hidden', 'true');
    btn.appendChild(label);
    btn.appendChild(chev);
    var anchor = w.querySelector('.aioo-facet-search__empty') || list;
    anchor.parentNode.insertBefore(btn, anchor.nextSibling);
    btn.addEventListener('click', function () {
      w.classList.toggle('aioo-list-expanded');
      refreshList(w);
    });
    return btn;
  }

  function refreshList(w) {
    var rows = listRows(w).filter(rowAvailable);
    var input = w.querySelector('.aioo-facet-search__input');
    var searchOn = !!(input && !input.closest('.aioo-facet-search').hidden);
    var q = searchOn ? normText(input.value) : '';
    var expanded = w.classList.contains('aioo-list-expanded');
    var hiddenN = 0;
    var matches = 0;
    var idx = 0;
    rows.forEach(function (r) {
      var match = !q || normText(rowText(r)).indexOf(q) !== -1;
      r.classList.toggle('aioo-facet-row--nomatch', !match);
      if (!match) {
        r.classList.remove('aioo-facet-row--collapsed');
        return;
      }
      matches += 1;
      var checked = !!r.querySelector('input:checked');
      var collapse = !q && !expanded && !checked && idx >= LIST_COLLAPSE_AT;
      idx += 1;
      r.classList.toggle('aioo-facet-row--collapsed', collapse);
      if (collapse) hiddenN += 1;
    });
    var more = w.querySelector('.aioo-facet-more');
    if (more) {
      var showMore = !q && rows.length > LIST_COLLAPSE_AT && (hiddenN > 0 || expanded);
      setHidden(more, !showMore);
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      setText(more.querySelector('.aioo-facet-more__label'), expanded
        ? i18n('less', 'Rādīt mazāk')
        : i18n('more', 'Rādīt vēl (@@)').replace(/@@/g, String(hiddenN)));
    }
    setHidden(w.querySelector('.aioo-facet-search__empty'), !(q && matches === 0));
  }

  function enhanceLongLists(root) {
    qsa('.sidebar_widget.filterBox', root).forEach(function (w) {
      if (w.matches(LIST_SKIP_SEL)) return;
      var list = w.querySelector('ul.flOpt');
      if (!list) return;
      var count = listRows(w).filter(rowAvailable).length;
      var search = w.querySelector('.aioo-facet-search');
      if (count > LIST_SEARCH_AT && !search) search = buildListSearch(w, list);
      if (search) setHidden(search, count <= LIST_SEARCH_AT);
      if (count > LIST_COLLAPSE_AT && !w.querySelector('.aioo-facet-more')) buildListMore(w, list);
      refreshList(w);
    });
  }

  /* ---- Price: native dual range + "No/Līdz" fields, one source of truth = the number fields ---- */
  function paintPrice(box) {
    var lo = box.querySelector('[data-bound="min"]');
    var hi = box.querySelector('[data-bound="max"]');
    var fill = box.querySelector('.aioo-price-range__fill');
    var max = parseFloat(box.getAttribute('data-max')) || 0;
    if (!lo || !hi || !fill || !(max > 0)) return;
    fill.style.left = (Math.min(+lo.value, max) / max) * 100 + '%';
    fill.style.right = (100 - (Math.min(+hi.value, max) / max) * 100) + '%';
  }

  function initPriceRanges(root) {
    qsa('[data-aioo-price-range]', root).forEach(function (box) {
      if (box.getAttribute('data-aioo-bound') === '1') {
        paintPrice(box);
        return;
      }
      var widget = box.closest('.flPrice');
      var lo = box.querySelector('[data-bound="min"]');
      var hi = box.querySelector('[data-bound="max"]');
      var gte = widget && qsa('.prRange', widget).filter(function (n) { return /\.gte$/.test(n.name); })[0];
      var lte = widget && qsa('.prRange', widget).filter(function (n) { return /\.lte$/.test(n.name); })[0];
      var max = parseFloat(box.getAttribute('data-max')) || 0;
      if (!lo || !hi || !gte || !lte || !(max > 0)) {
        box.hidden = true;
        return;
      }
      box.setAttribute('data-aioo-bound', '1');
      var timer = null;
      function commit(field) {
        clearTimeout(timer);
        timer = setTimeout(function () {
          field.dispatchEvent(new Event('change', { bubbles: true }));
        }, PRICE_COMMIT_MS);
      }
      function fromFields() {
        var a = gte.value === '' ? 0 : Math.max(0, Math.min(max, parseFloat(gte.value) || 0));
        var b = lte.value === '' ? max : Math.max(0, Math.min(max, parseFloat(lte.value) || 0));
        lo.value = String(Math.min(a, b));
        hi.value = String(Math.max(a, b));
        paintPrice(box);
      }
      lo.addEventListener('input', function () {
        if (+lo.value > +hi.value) lo.value = hi.value;
        gte.value = +lo.value > 0 ? String(Math.round(+lo.value)) : '';
        paintPrice(box);
        commit(gte);
      });
      hi.addEventListener('input', function () {
        if (+hi.value < +lo.value) hi.value = lo.value;
        lte.value = +hi.value < max ? String(Math.round(+hi.value)) : '';
        paintPrice(box);
        commit(lte);
      });
      [gte, lte].forEach(function (field) {
        field.addEventListener('input', fromFields);
        /* Runs before the theme's delegated document handler builds the URL. */
        field.addEventListener('change', function () {
          var a = parseFloat(gte.value);
          var b = parseFloat(lte.value);
          if (!isNaN(a) && !isNaN(b) && a > b) {
            gte.value = String(b);
            lte.value = String(a);
          }
          if (!isNaN(b) && b >= max) lte.value = '';
          if (!isNaN(a) && a <= 0) gte.value = '';
          fromFields();
        });
      });
      fromFields();
    });
  }
  /* theme.min.js still calls priceSlider() after its own swaps. */
  window.priceSlider = function () { initPriceRanges(document); };

  /* Enter in a facet field would submit the GET form (full reload). */
  function bindFormSubmit() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('#CollectionFiltersForm, .flForm')) return;
      e.preventDefault();
      var a = document.activeElement;
      if (a && a.classList && a.classList.contains('prRange')) {
        a.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, true);
  }

  /* ---- Accordion state: user choice > group with a selected value > priority default ---- */
  function bindAccordionMemory() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.filterBox .filter-widget-toggle');
      if (!btn) return;
      var w = btn.closest('.sidebar_widget');
      var k = w && widgetKey(w);
      if (!k) return;
      setTimeout(function () { core.userOpen[k] = btn.classList.contains('active'); }, 0);
    });
  }

  function applyAccordionState() {
    qsa('.sidebar_widget.filterBox').forEach(function (widget) {
      var panel = widget.querySelector('.filter-widget-panel.collapsible, .filterDD, .widget-content.filter-widget-panel');
      if (!panel) return;
      var toggle = widget.querySelector('.filter-widget-toggle');
      var k = widgetKey(widget);
      var open;
      if (k && Object.prototype.hasOwnProperty.call(core.userOpen, k)) {
        open = core.userOpen[k];
      } else if (widget.matches('.flPrice, .filter-color, .mbf-sidebar-widget, .filter-brand-pop, [data-aioo-facet-key*="custom.subtype"]') || widget.querySelector('input.custCheck:checked')) {
        open = true;
      } else {
        open = panel.classList.contains('is-open');
      }
      panel.classList.toggle('is-open', open);
      if (open) panel.classList.remove('hide');
      if (toggle) {
        toggle.classList.toggle('active', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    });
  }

  /* ---- Active facet chips above the grid ---- */
  function polishActiveFacets() {
    qsa('.active-facets').forEach(function (box) {
      var hasValueChips = false;
      qsa('a.actFilter', box).forEach(function (a, i) {
        if (a.querySelector('.at-times-l, .at-times, .at.at-times-l')) hasValueChips = true;
        if (i === 0 && !a.querySelector('.at-times-l')) a.classList.add('aioo-facets-clear-all');
        else a.classList.add('aioo-facets-chip');
      });
      box.classList.toggle('is-empty', !hasValueChips);
      setHidden(box, !hasValueChips);
    });
  }

  /* Hide variant-option facet values that have no in-stock variant.
     Shopify counts products, not inventory, so OOS colours/sizes still
     arrive with count>0. Liquid already drops them when the whole result
     set fits on one page (data-aioo-instock-complete=1). Multi-page
     collections fetch a compact ?view=aioo-instock-opts scan. */
  var AIOO_INSTOCK_MAX_SCAN_PAGES = 3; /* 250/page in instock view => 750 SKUs max */

  function sidebarInstockKeysFromDom() {
    var tags = qs('.sidebar_tags');
    if (!tags || tags.getAttribute('data-aioo-instock-complete') !== '1') return null;
    var raw = tags.getAttribute('data-aioo-instock-keys');
    if (!raw) return null;
    try {
      var keys = JSON.parse(raw);
      return Array.isArray(keys) ? keys.filter(function (k) { return k && String(k).trim(); }) : null;
    } catch (_e) {
      return null;
    }
  }

  function facetToken(label) {
    var s = String(label || '').replace(/\(\d+\)\s*$/, '').trim();
    if (!s) return '';
    if (window.AIOO_COLOR && typeof window.AIOO_COLOR.normKey === 'function') {
      return window.AIOO_COLOR.normKey(s).replace(/\s+/g, ',');
    }
    return s.toLowerCase().trim().replace(/\s*,\s*/g, ',').replace(/\s*\/\s*/g, ',');
  }

  function tokenMatchesInstockSet(label, set) {
    var t = facetToken(label);
    if (!t) return false;
    if (set[t]) return true;
    var parts = t.split(',');
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim();
      if (p && set[p]) return true;
    }
    return false;
  }

  function rowInstockTokens(row) {
    var master = row.querySelector('input.aioo-color-filter__master');
    if (master) {
      var raw = master.getAttribute('data-aioo-facet-tokens') || '';
      var list = raw ? raw.split('\n').filter(Boolean) : [];
      if (!list.length) list.push(row.getAttribute('title') || master.getAttribute('aria-label') || '');
      return list;
    }
    var lab = row.querySelector('.aioo-color-filter__label');
    return [(lab && lab.textContent) || row.getAttribute('title') || ''];
  }

  function applyInstockKeys(keys) {
    var set = {};
    (keys || []).forEach(function (k) {
      if (!k || !String(k).trim()) return;
      var t = facetToken(k);
      if (t) set[t] = true;
    });
    qsa('.sidebar_widget.filterBox label.fl-checkbox, .sidebar_widget.filterBox label.aioo-color-filter__row').forEach(function (row) {
      if (row.closest('.mbf-sidebar-widget, .model-links-widget, .condition-filter-widget, .aioo-color-filter__originals')) return;
      var input = row.querySelector('input[type="checkbox"], input[type="radio"]');
      if (!input) return;
      var isMaster = input.classList.contains('aioo-color-filter__master');
      var isOption = (input.name || '').indexOf('filter.v.option.') !== -1;
      if (!isMaster && !isOption) return;
      if (input.checked) {
        setHidden(row, false);
        row.classList.remove('aioo-oos-facet');
        return;
      }
      var tokens = rowInstockTokens(row);
      var inStock = false;
      for (var i = 0; i < tokens.length; i++) {
        if (tokenMatchesInstockSet(tokens[i], set)) {
          inStock = true;
          break;
        }
      }
      row.classList.toggle('aioo-oos-facet', !inStock);
      setHidden(row, !inStock);
    });
    qsa('.sidebar_widget.filterBox').forEach(function (widget) {
      if (!widget.querySelector('input[name*="filter.v.option."], input.aioo-color-filter__master')) return;
      var shown = 0;
      qsa('label.fl-checkbox, label.aioo-color-filter__row', widget).forEach(function (el) {
        if (el.closest('.aioo-color-filter__originals')) return;
        if (el.classList.contains('aioo-oos-facet') || el.hidden) return;
        shown += 1;
      });
      if (shown === 0) setHidden(widget, true);
    });
    applyColorSwatchCollapse(document);
    enhanceLongLists(document);
  }

  function parseInstockJson(text) {
    var raw = String(text || '').replace(/^\uFEFF/, '').trim();
    try {
      return JSON.parse(raw);
    } catch (_e) {
      var m = raw.match(/\{[\s\S]*\}/);
      if (!m) return null;
      try { return JSON.parse(m[0]); } catch (_e2) { return null; }
    }
  }

  function instockScanUrl(page) {
    var u = new URL(window.location.href);
    var form = qs('.flForm');
    if (form) {
      var names = {};
      qsa('input.custCheck', form).forEach(function (inp) {
        if (inp.name) names[inp.name] = true;
      });
      Object.keys(names).forEach(function (n) { u.searchParams.delete(n); });
      qsa('input.custCheck:checked', form).forEach(function (inp) {
        if (inp.name) u.searchParams.append(inp.name, inp.value);
      });
    }
    u.searchParams.set('view', 'aioo-instock-opts');
    if (page && page > 1) u.searchParams.set('page', String(page));
    else u.searchParams.delete('page');
    return u;
  }

  function syncOosOptionFacets() {
    var tags = qs('.sidebar_tags');
    if (!tags) return;
    var path = window.location.pathname || '';
    if (path.indexOf('/collections/') === -1 && path.indexOf('/search') === -1) return;
    var url = instockScanUrl(1);
    var cacheKey = url.pathname + url.search;
    syncOosOptionFacets._cache = syncOosOptionFacets._cache || {};
    if (syncOosOptionFacets._cache[cacheKey]) {
      applyInstockKeys(syncOosOptionFacets._cache[cacheKey]);
      return;
    }
    var domKeys = sidebarInstockKeysFromDom();
    if (domKeys && domKeys.length) {
      syncOosOptionFacets._cache[cacheKey] = domKeys;
      applyInstockKeys(domKeys);
      return;
    }
    if (syncOosOptionFacets._idle) clearTimeout(syncOosOptionFacets._idle);
    syncOosOptionFacets._idle = setTimeout(function () {
      fetchInstockAndApply(cacheKey, url);
    }, 450);
  }

  function fetchInstockAndApply(cacheKey, url) {
    if (syncOosOptionFacets._pending === cacheKey) return;
    if (syncOosOptionFacets._abort) {
      try { syncOosOptionFacets._abort.abort(); } catch (_e) { /* ignore */ }
    }
    var abortCtrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    syncOosOptionFacets._abort = abortCtrl;
    syncOosOptionFacets._pending = cacheKey;
    var fetchOpts = { credentials: 'same-origin' };
    if (abortCtrl) fetchOpts.signal = abortCtrl.signal;
    fetch(url.toString(), fetchOpts)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var json = parseInstockJson(text);
        if (!json) return null;
        var keys = json.keys || [];
        if (json.complete) return keys;
        if (!json.pages || json.pages > AIOO_INSTOCK_MAX_SCAN_PAGES) return null;
        var pages = Math.min(json.pages, AIOO_INSTOCK_MAX_SCAN_PAGES);
        var chain = Promise.resolve(keys);
        for (var p = 2; p <= pages; p++) {
          (function (pageNum) {
            chain = chain.then(function (acc) {
              return fetch(instockScanUrl(pageNum).toString(), fetchOpts)
                .then(function (r2) { return r2.text(); })
                .then(function (text2) {
                  var json2 = parseInstockJson(text2);
                  if (!json2) return acc;
                  return acc.concat(json2.keys || []);
                });
            });
          })(p);
        }
        return chain;
      })
      .then(function (keys) {
        if (!keys) return;
        syncOosOptionFacets._cache[cacheKey] = keys;
        applyInstockKeys(keys);
      })
      .catch(function () { /* keep Shopify chips */ })
      .then(function () {
        if (syncOosOptionFacets._pending === cacheKey) syncOosOptionFacets._pending = null;
      });
  }

  function onSidebarMutated() {
    enhanceFacetLists();
    initPriceRanges(document);
    enhanceLongLists(document);
    polishActiveFacets();
    applyAccordionState();
    updateChrome();
    syncOosOptionFacets();
  }

  function watchSidebar() {
    var tags = qs('.sidebar_tags');
    if (!tags || !window.MutationObserver) return;
    var t = null;
    var obs = new MutationObserver(function () {
      if (t) clearTimeout(t);
      t = setTimeout(onSidebarMutated, 30);
    });
    obs.observe(tags, { childList: true, subtree: true });
  }

  /* ---- Sidebar state that must survive a re-render ---- */
  function captureSidebarState() {
    var s = { widgets: {}, focus: null, scroll: [0, 0] };
    qsa('.sidebar_tags .sidebar_widget').forEach(function (w) {
      var k = widgetKey(w);
      if (!k) return;
      var search = w.querySelector('.aioo-facet-search__input');
      s.widgets[k] = {
        listExpanded: w.classList.contains('aioo-list-expanded'),
        swatchesExpanded: w.classList.contains('swatches-expanded'),
        query: search ? search.value : ''
      };
    });
    var a = document.activeElement;
    if (a && a.closest && a.closest('.sidebar_tags')) {
      var w = a.closest('.sidebar_widget');
      var kind = '';
      if (a.classList.contains('aioo-facet-search__input')) kind = 'search';
      else if (a.classList.contains('filter-widget-toggle')) kind = 'toggle';
      else if (a.classList.contains('aioo-facet-more')) kind = 'more';
      else if (a.classList.contains('aioo-color-collapse')) kind = 'colors';
      s.focus = {
        key: w ? widgetKey(w) : '',
        kind: kind,
        name: a.name || '',
        value: a.value,
        label: a.getAttribute('aria-label') || '',
        bound: a.getAttribute('data-bound') || '',
        id: a.id || ''
      };
    }
    var d = getDrawer();
    var t = qs('.sidebar_tags');
    s.scroll = [d ? d.scrollTop : 0, t ? t.scrollTop : 0];
    return s;
  }

  function findFocusTarget(f) {
    var w = null;
    if (f.key) {
      w = qsa('.sidebar_tags .sidebar_widget').filter(function (el) { return widgetKey(el) === f.key; })[0] || null;
    }
    var scope = w || qs('.sidebar_tags');
    if (!scope) return null;
    if (f.kind === 'search') return scope.querySelector('.aioo-facet-search__input');
    if (f.kind === 'toggle') return scope.querySelector('.filter-widget-toggle');
    if (f.kind === 'more') return scope.querySelector('.aioo-facet-more:not([hidden])');
    if (f.kind === 'colors') return scope.querySelector('.aioo-color-collapse:not([hidden])');
    if (f.bound) return scope.querySelector('[data-bound="' + f.bound + '"]');
    var inputs = qsa('input', scope);
    var hit = null;
    if (f.name) hit = inputs.filter(function (i) { return i.name === f.name && i.value === f.value; })[0];
    if (!hit && f.label) hit = inputs.filter(function (i) { return i.getAttribute('aria-label') === f.label; })[0];
    if (!hit && f.id) hit = document.getElementById(f.id);
    return hit || null;
  }

  function restoreSidebarState(s) {
    if (!s) return;
    qsa('.sidebar_tags .sidebar_widget').forEach(function (w) {
      var st = s.widgets[widgetKey(w)];
      if (!st) return;
      w.classList.toggle('aioo-list-expanded', !!st.listExpanded);
      w.classList.toggle('swatches-expanded', !!st.swatchesExpanded);
      var input = w.querySelector('.aioo-facet-search__input');
      if (input && st.query) input.value = st.query;
      if (w.querySelector('.aioo-facet-more, .aioo-facet-search')) refreshList(w);
    });
    applyColorSwatchCollapse(document);
    var d = getDrawer();
    var t = qs('.sidebar_tags');
    if (d) d.scrollTop = s.scroll[0];
    if (t) t.scrollTop = s.scroll[1];
    if (s.focus) {
      var el = findFocusTarget(s.focus);
      if (el) {
        try { el.focus({ preventScroll: true }); } catch (_e) { el.focus(); }
      }
    }
  }

  /* ---- DOM swap ---- */
  function htmlInto(target, html) {
    var $ = window.jQuery;
    /* jQuery .html() re-executes inline <script> (the mbf-root init needs that). */
    if ($) $(target).html(html);
    else target.innerHTML = html;
  }

  function swapInner(doc, selector) {
    var source = doc.querySelector(selector);
    var target = qs(selector);
    if (source && target) htmlInto(target, source.innerHTML);
  }

  function swapNode(doc, selector, insertAfterSel) {
    var source = doc.querySelector(selector);
    var target = qs(selector);
    if (source && target) {
      target.parentNode.replaceChild(document.importNode(source, true), target);
    } else if (source && insertAfterSel) {
      var anchor = qs(insertAfterSel);
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(document.importNode(source, true), anchor.nextSibling);
    } else if (target && !source) {
      target.innerHTML = '';
      target.hidden = true;
    }
  }

  function syncPickerCta(doc) {
    var freshCta = doc.getElementById('aioo-picker-cta');
    var liveCta = document.getElementById('aioo-picker-cta');
    if (freshCta && liveCta) {
      liveCta.setAttribute('data-product-count', freshCta.getAttribute('data-product-count') || '0');
    }
    var freshChipCount = doc.getElementById('aioo-picker-count');
    var liveChipCount = document.getElementById('aioo-picker-count');
    if (freshChipCount && liveChipCount) {
      liveChipCount.textContent = freshChipCount.textContent;
      liveChipCount.hidden = freshChipCount.hidden;
    }
  }

  function swapSidebar(doc) {
    var fresh = doc.querySelector('.sidebar_tags');
    var live = qs('.sidebar_tags');
    if (!fresh || !live) return false;
    ['data-url', 'data-aioo-instock-complete', 'data-aioo-instock-keys'].forEach(function (a) {
      var v = fresh.getAttribute(a);
      if (v == null) live.removeAttribute(a);
      else live.setAttribute(a, v);
    });
    htmlInto(live, fresh.innerHTML);
    return true;
  }

  function applyDoc(doc) {
    if (!doc) return;
    var snap = captureSidebarState();

    /* Picker CTA first: the mbf-root inline script inside .sidebar_tags reads
       #aioo-picker-count synchronously the moment it is inserted. */
    syncPickerCta(doc);
    swapInner(doc, '.productList .grid-products');
    swapInner(doc, '.productList .list-view-items');
    swapNode(doc, '.filters-toolbar__product-count');
    swapNode(doc, '.active-facets', '#FilterBar');

    var freshPagOuter = doc.querySelector('.infinitpaginOuter');
    if (freshPagOuter && freshPagOuter.innerHTML.trim()) {
      swapInner(doc, '.infinitpaginOuter');
    } else {
      var pagin = qs('.infinitpagin');
      if (pagin) pagin.remove();
    }
    var freshPag = doc.querySelector('.pagination');
    var livePag = qs('.pagination');
    if (freshPag && livePag) {
      livePag.innerHTML = freshPag.innerHTML;
      livePag.style.display = freshPag.innerHTML.trim() ? '' : 'none';
    }
    if (typeof window.AIOO_syncLoadMoreFromDoc === 'function') {
      try { window.AIOO_syncLoadMoreFromDoc(doc); } catch (_lm) { /* ignore */ }
    }

    if (swapSidebar(doc)) {
      /* Rebuild everything in the same task as the swap so raw checkboxes never paint. */
      if (typeof window.AIOO_initColorFilterGroups === 'function') window.AIOO_initColorFilterGroups();
      enhanceFacetLists();
      initPriceRanges(document);
      var cached = syncOosOptionFacets._cache && syncOosOptionFacets._cache[(function () {
        var u = instockScanUrl(1);
        return u.pathname + u.search;
      })()];
      var domKeys = sidebarInstockKeysFromDom();
      if (cached || (domKeys && domKeys.length)) applyInstockKeys(cached || domKeys);
      enhanceLongLists(document);
      applyAccordionState();
      restoreSidebarState(snap);
      qsa('.mbf-root[id]').forEach(function (el) {
        el.removeAttribute('data-mbf-init');
        if (window.AIOO_initMbf) window.AIOO_initMbf(el.id);
      });
    }

    polishActiveFacets();
    if (window.theme && window.theme.mlcurrency && typeof window.currenciesChange === 'function') {
      window.currenciesChange('sapn.money');
    }
    if (qs('.infinitpagin a.infinite') && typeof window.infiniteScroll === 'function') window.infiniteScroll();
    if (typeof window.loadMoreBtn === 'function') window.loadMoreBtn();
    if (window.lazySizes && window.lazySizes.loader && typeof window.lazySizes.loader.checkElems === 'function') {
      window.lazySizes.loader.checkElems();
    }
    document.dispatchEvent(new CustomEvent('aioo:collection-filters-updated'));
  }
  window.AIOO_applyCollectionResponseToDom = applyDoc;

  /* ---- Fetch engine ---- */
  function sectionId() {
    var host = qs('.productList');
    var sec = host && host.closest('[id^="shopify-section-"]');
    return sec ? sec.id.replace(/^shopify-section-/, '') : '';
  }

  /* Jauns/Mazlietots telefons is the same choice as the Stāvoklis tabs.
     Carrying ?condition=used onto a "Jauns telefons" result hid every card. */
  function phoneKindFromSubtype(params) {
    var wantsNew = false;
    var wantsUsed = false;
    params.getAll('filter.p.m.custom.subtype').forEach(function (v) {
      var s = String(v || '').toLowerCase();
      if (s.indexOf('jauns telefons') !== -1) wantsNew = true;
      if (s.indexOf('mazlietots telefons') !== -1 || s.indexOf('mazlietoti telefoni') !== -1) wantsUsed = true;
    });
    if (wantsNew && wantsUsed) return 'all';
    if (wantsNew) return 'new';
    if (wantsUsed) return 'used';
    return '';
  }

  function buildTarget(url, opts) {
    var t = new URL(url, window.location.href);
    t.searchParams.delete('section_id');
    if (opts.fromPop) return t;
    t.searchParams.delete('page');
    if (!opts.reset) {
      var cur = new URL(window.location.href);
      ['cp', 'condition'].forEach(function (k) {
        if (cur.searchParams.has(k) && !t.searchParams.has(k)) t.searchParams.set(k, cur.searchParams.get(k));
      });
    }
    var kind = phoneKindFromSubtype(t.searchParams);
    if (kind === 'all') t.searchParams.delete('condition');
    else if (kind) t.searchParams.set('condition', kind);
    return t;
  }

  function writeHistory(t, replace) {
    var rel = t.pathname + t.search + t.hash;
    if (rel === window.location.pathname + window.location.search + window.location.hash) return;
    try {
      window.history[replace ? 'replaceState' : 'pushState']({ aiooFilter: true }, '', rel);
    } catch (_e) { /* ignore */ }
  }

  function fetchDoc(url, ctrl) {
    var opts = { credentials: 'same-origin' };
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(url.toString(), opts).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  function trackApplied(t) {
    if (typeof window.AIOO_trackUxEvent !== 'function') return;
    var filterType = 'price';
    t.searchParams.forEach(function (_v, k) {
      if (k.indexOf('filter.v.option') !== -1) filterType = 'color';
      if (k.indexOf('compatible_model') !== -1 || k === 'aioo_model') filterType = 'model';
    });
    window.AIOO_trackUxEvent('filter_applied', { filter_type: filterType, page_path: window.location.pathname });
    if (!qsa('.productList .grid-products .gitem:not(.small--tc)').length) {
      window.AIOO_trackUxEvent('filter_empty_result', { page_path: window.location.pathname });
    }
  }

  function run(t, seq) {
    core.timer = null;
    if (core.ctrl) {
      try { core.ctrl.abort(); } catch (_a) { /* ignore */ }
    }
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    core.ctrl = ctrl;
    clearTimeout(core.watchdog);
    core.watchdog = setTimeout(function () {
      if (seq !== core.seq) return;
      if (ctrl) ctrl.abort();
      window.location.assign(t.href);
    }, REQUEST_TIMEOUT_MS);

    var sid = sectionId();
    var first = t;
    if (sid) {
      first = new URL(t.href);
      first.searchParams.set('section_id', sid);
    }
    return fetchDoc(first, ctrl)
      .then(function (doc) {
        if (sid && !doc.querySelector('.productList')) return fetchDoc(t, ctrl);
        return doc;
      })
      .then(function (doc) {
        if (seq !== core.seq) return;
        clearTimeout(core.watchdog);
        applyDoc(doc);
        core.lastUrl = t.pathname + t.search;
        trackApplied(t);
        updateChrome();
        announceResults();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (seq !== core.seq) return;
        clearTimeout(core.watchdog);
        window.location.assign(t.href);
      })
      .then(function () {
        if (seq === core.seq) clearCollectionChrome();
      });
  }

  /**
   * opts.fromPop  — back/forward: keep page, never touch history
   * opts.push     — false to skip history (default true)
   * opts.reset    — do not carry cp/condition from the current URL
   * opts.immediate — skip the debounce
   */
  function load(url, opts) {
    opts = opts || {};
    if (!url) return Promise.resolve();
    var t;
    try {
      t = buildTarget(url, opts);
    } catch (_e) {
      window.location.assign(url);
      return Promise.resolve();
    }
    var pending = !!core.timer;
    clearTimeout(core.timer);
    var seq = ++core.seq;
    setGridLoading(true);
    if (!opts.fromPop && opts.push !== false) writeHistory(t, pending);
    updateChrome();
    return new Promise(function (resolve) {
      core.timer = setTimeout(function () {
        if (seq !== core.seq) { resolve(); return; }
        run(t, seq).then(resolve, resolve);
      }, opts.immediate || opts.fromPop ? 0 : DEBOUNCE_MS);
    });
  }

  window.AIOO_facets = { load: load, applyDoc: applyDoc, reset: resetAll, refresh: onSidebarMutated, urlFromForm: formFilterUrl };

  /* theme.min.js assigns the global ajaxfilter inside its init; an accessor keeps every
     caller (form change, chip click, sort) on this engine no matter when that runs. */
  function installAjaxfilter() {
    function softAjaxfilter(url) { return load(url, {}); }
    softAjaxfilter._aiooSoft = true;
    try {
      Object.defineProperty(window, 'ajaxfilter', {
        configurable: true,
        get: function () { return softAjaxfilter; },
        set: function () { /* theme version ignored */ }
      });
    } catch (_e) {
      window.ajaxfilter = softAjaxfilter;
    }
  }

  function watchPopState() {
    window.addEventListener('popstate', function () {
      clearCollectionChrome({ closeDrawer: true });
      setTimeout(function () {
        if (window.AIOO_bfcacheHold) return;
        var path = window.location.pathname || '';
        if (path.indexOf('/collections/') === -1 && path.indexOf('/search') === -1) return;
        if (!qs('.productList')) return;
        if (window.location.pathname + window.location.search === core.lastUrl) return;
        load(window.location.href, { fromPop: true });
      }, 60);
    });
  }

  /* Client-side brand / condition filters (phones) change the visible set without a fetch. */
  function bindClientFilters() {
    document.addEventListener('aioo:client-filter-changed', function () {
      updateChrome();
      announceResults();
    });
  }

  installAjaxfilter();

  function init() {
    watchDrawer();
    bindApplyFooter();
    bindReset();
    bindChipToggle();
    bindFormSubmit();
    bindAccordionMemory();
    bindClientFilters();
    watchSidebar();
    onSidebarMutated();
    syncVisibleProductCount();
    watchPopState();
    updateStickyTop();
    document.addEventListener('aioo:load-more-appended', syncVisibleProductCount);
    document.addEventListener('aioo:collection-filters-updated', function () {
      syncVisibleProductCount();
    });
    var scrollTick = false;
    window.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = true;
      window.requestAnimationFrame(function () {
        scrollTick = false;
        updateStickyTop();
      });
    }, { passive: true });
    var resizeTick = null;
    window.addEventListener('resize', function () {
      if (resizeTick) clearTimeout(resizeTick);
      resizeTick = setTimeout(function () {
        applyColorSwatchCollapse(document);
        syncDrawerChrome();
        updateStickyTop();
      }, 80);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
